import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  CheckCircle, 
  XCircle, 
  ArrowRight, 
  ArrowLeft,
  Lightbulb,
  Target,
  Clock,
  Users
} from 'lucide-react';
import { EducationalLesson as EducationalLessonType } from '../types/gameModes';

interface EducationalLessonProps {
  lesson: EducationalLessonType;
  onComplete: (score: number) => void;
  onSkip: () => void;
}

const EducationalLesson: React.FC<EducationalLessonProps> = ({ lesson, onComplete, onSkip }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const currentQuestion = lesson.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === lesson.questions.length - 1;

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calculate score
      const correctAnswers = selectedAnswers.filter((answer, index) => 
        answer === lesson.questions[index].correctAnswer
      ).length;
      const finalScore = Math.round((correctAnswers / lesson.questions.length) * 100);
      setScore(finalScore);
      setShowResults(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleComplete = () => {
    onComplete(score);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 80) return 'Excellent! You have a strong understanding of this topic.';
    if (score >= 60) return 'Good job! You understand the basics well.';
    return 'Keep learning! Review the material and try again.';
  };

  if (showResults) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-4xl mx-auto p-8 bg-slate-800 rounded-2xl border border-slate-700"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
              score >= 80 ? 'bg-green-500/20' : score >= 60 ? 'bg-yellow-500/20' : 'bg-red-500/20'
            }`}
          >
            <span className={`text-3xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </span>
          </motion.div>
          <h2 className="text-2xl font-bold text-white mb-2">Lesson Complete!</h2>
          <p className={`text-lg ${getScoreColor(score)}`}>
            {getScoreMessage(score)}
          </p>
        </div>

        {/* Question Results */}
        <div className="space-y-4 mb-8">
          {lesson.questions.map((question, index) => {
            const userAnswer = selectedAnswers[index];
            const isCorrect = userAnswer === question.correctAnswer;
            
            return (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-xl border-2 ${
                  isCorrect 
                    ? 'border-green-500/30 bg-green-500/10' 
                    : 'border-red-500/30 bg-red-500/10'
                }`}
              >
                <div className="flex items-start space-x-3">
                  {isCorrect ? (
                    <CheckCircle size={20} className="text-green-400 mt-1" />
                  ) : (
                    <XCircle size={20} className="text-red-400 mt-1" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-2">{question.question}</h4>
                    <div className="space-y-2">
                      {question.options.map((option, optionIndex) => (
                        <div
                          key={optionIndex}
                          className={`p-2 rounded-lg text-sm ${
                            optionIndex === question.correctAnswer
                              ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                              : optionIndex === userAnswer
                              ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                              : 'bg-slate-700/50 text-slate-300'
                          }`}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-sm text-slate-300">
                        <strong>Explanation:</strong> {question.explanation}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Reflection Points */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
            <Lightbulb size={24} className="text-yellow-400" />
            <span>Reflection Points</span>
          </h3>
          <div className="space-y-3">
            {lesson.reflectionPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="p-4 bg-slate-700/50 rounded-xl border border-slate-600"
              >
                <p className="text-slate-300">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onSkip}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors duration-300"
          >
            Skip Lesson
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleComplete}
            className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
          >
            <span>Continue Journey</span>
            <ArrowRight size={20} />
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto p-8 bg-slate-800 rounded-2xl border border-slate-700"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
          <BookOpen size={32} className="text-emerald-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">{lesson.title}</h1>
        <p className="text-slate-300 text-lg">{lesson.content}</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-slate-400">Question {currentQuestionIndex + 1} of {lesson.questions.length}</span>
          <span className="text-sm text-slate-400">
            {Math.round(((currentQuestionIndex + 1) / lesson.questions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestionIndex + 1) / lesson.questions.length) * 100}%` }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2 rounded-full"
          />
        </div>
      </div>

      {/* Question */}
      <motion.div
        key={currentQuestionIndex}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="mb-8"
      >
        <h2 className="text-xl font-semibold text-white mb-6">{currentQuestion.question}</h2>
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAnswerSelect(index)}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 ${
                selectedAnswers[currentQuestionIndex] === index
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                  : 'border-slate-600 bg-slate-700/50 text-slate-300 hover:border-slate-500 hover:bg-slate-700/70'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedAnswers[currentQuestionIndex] === index
                    ? 'border-emerald-500 bg-emerald-500'
                    : 'border-slate-500'
                }`}>
                  {selectedAnswers[currentQuestionIndex] === index && (
                    <CheckCircle size={16} className="text-white" />
                  )}
                </div>
                <span>{option}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
            currentQuestionIndex === 0
              ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
              : 'bg-slate-700 hover:bg-slate-600 text-white'
          }`}
        >
          <ArrowLeft size={20} />
          <span>Previous</span>
        </motion.button>

        <div className="flex space-x-2">
          {lesson.questions.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full ${
                index === currentQuestionIndex
                  ? 'bg-emerald-500'
                  : index < currentQuestionIndex
                  ? 'bg-emerald-400'
                  : 'bg-slate-600'
              }`}
            />
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleNext}
          disabled={selectedAnswers[currentQuestionIndex] === undefined}
          className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
            selectedAnswers[currentQuestionIndex] === undefined
              ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
          }`}
        >
          <span>{isLastQuestion ? 'Finish' : 'Next'}</span>
          <ArrowRight size={20} />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default EducationalLesson;
